import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { card, chip, ib, table } from "../../ui/record";
import { field, refuse, select } from "../clients/forms";

// Payroll › Pay rates (brief 1E-16 addition, mockup v74 homecare/payroll): each title's pay rate
// card, used when a caregiver has no pay rate of their own. The same card and behavior brief 1E-15
// built as Pay by title on the rates screen: the pay rate now (Now) and any change to come (Next),
// a pencil per row that starts a change on a date, and + for a new one. Seen with the Money switch;
// changed with the Edit pay rates switch. A caregiver's own pay rates are in their pay package.

interface Answer {
  today: string;
  seesMoney: boolean;
  canEditPay: boolean;
  services: { id: string; name: string }[];
  titles: string[];
  roleDefaults: PayByTitle[];
}

interface PayByTitle {
  id: string;
  title: string;
  serviceId: string | null;
  service: string;
  rate: number;
  per: string;
  words: string;
  starts: string;
  live: boolean;
  /** The first day a change can start. */
  earliest: string;
}

/** The Pay rates card, or nothing for someone who doesn't see pay. Redraws itself after a save. */
export async function payRatesCard(): Promise<HTMLElement | null> {
  const res = await api<Answer>("GET", "/api/pay-rates");
  if (!res.ok || !res.body.seesMoney) return null;
  const host = el("div");
  const draw = (x: Answer) => {
    const cols: { h: string; f: (r: PayByTitle) => Node | string; num?: boolean }[] = [
      { h: "Title", f: (r) => el("b", {}, r.title) },
      { h: "Service", f: (r) => r.service },
      { h: "Pay rate", num: true, f: (r) => r.words },
      {
        h: "From",
        f: (r) => el("span", {}, r.starts, " ", r.live ? chip("Now", "ok") : chip("Next", "info")),
      },
    ];
    if (x.canEditPay)
      cols.push({
        h: "",
        num: true,
        f: (r) => ib("pencil", `Change the pay rate for ${r.title}`, () => payModal(x, r)),
      });
    host.replaceChildren(
      card(
        "Pay rates",
        "Each title's pay rate card, used when a caregiver has no pay rate of their own.",
        x.canEditPay ? [ib("plus", "Add a title's pay rate", () => payModal(x))] : [],
        x.roleDefaults.length
          ? table(cols, x.roleDefaults, undefined, "Pay rates")
          : el("p", { class: "muted" }, "No pay rates by title yet."),
      ),
    );
  };
  const reload = async () => {
    const again = await api<Answer>("GET", "/api/pay-rates");
    if (again.ok) draw(again.body);
  };

  /** A title's pay rate: a new one, or (the pencil) a change that starts on a date. */
  function payModal(x: Answer, row?: PayByTitle) {
    const title = select(
      "Title",
      x.titles.map((t) => [t, t] as const),
      row?.title ?? x.titles[0],
    );
    const service = select(
      "Service",
      [["", "Any service"], ...x.services.map((s) => [s.id, s.name] as const)],
      row?.serviceId ?? "",
    );
    // A change keeps its title and service; only the pay rate and the day it starts change.
    title.input.disabled = !!row;
    service.input.disabled = !!row;
    const rate = field("Pay rate ($)", row ? row.rate.toFixed(2) : "", {
      inputmode: "decimal",
      placeholder: "19.00",
    });
    const per = select(
      "Per",
      [
        ["hour", "Per hour"],
        ["visit", "Per visit"],
      ],
      row?.per ?? "hour",
    );
    const from = field("Starts", row && row.earliest > x.today ? row.earliest : x.today, {
      type: "date",
    });
    const save = el("button", { type: "button", class: "btn pri" }, icon("check"), " Save");
    const m = openModal({
      title: row ? `Change the pay rate for ${row.title}` : "A title's pay rate",
      sub: row
        ? "A change starts on a date; visits already paid keep their pay."
        : "The pay rate for a title when a caregiver has no pay rate of their own. It ends the one before.",
      body: [
        el(
          "div",
          { class: "form formgrid", style: "max-width:none" },
          title.el,
          service.el,
          rate.el,
          per.el,
          from.el,
        ),
      ],
      actions: [save],
    });
    save.addEventListener("click", async () => {
      save.disabled = true;
      const r = await api("POST", "/api/rates/pay", {
        level: "role",
        title: title.input.value,
        serviceId: service.input.value || null,
        rate: rate.input.value,
        per: per.input.value,
        from: from.input.value,
      });
      save.disabled = false;
      if (!r.ok) return refuse(m.note, r.body.error);
      m.close();
      toast(
        "Pay rate saved",
        `${title.input.value} · $${Number(rate.input.value).toFixed(2)} from ${from.input.value}`,
      );
      await reload();
    });
  }

  draw(res.body);
  return host;
}
